/* @moldura padrao */
/**
 * page.tsx (meu-perfil) — O usuário edita os próprios dados e troca a senha.
 *
 * Dados (nome, e-mail, foto) via PUT /usuarios/me; senha via POST /usuarios/me/senha
 * (confere a atual). Usuário/perfil/loja são só leitura (mudança é do admin).
 */
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';
import { comprimirImagem } from '@/lib/imagem';
import { useAuth } from '@/auth/auth-context';
import { CustomMolduraPagina } from '@/components/CustomMolduraPagina';

interface UploadFoto {
  arquivo: string;
  url: string;
}

export function MeuPerfilPage() {
  const { usuario, recarregar } = useAuth();

  const [nome, setNome] = useState(usuario?.nome ?? '');
  const [email, setEmail] = useState(usuario?.email ?? '');
  const [foto, setFoto] = useState<string | null>(
    usuario?.foto?.replace(/^\/uploads\//, '') ?? null,
  );
  const [salvandoDados, setSalvandoDados] = useState(false);
  const [enviandoFoto, setEnviandoFoto] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // Troca de senha
  const [atual, setAtual] = useState('');
  const [nova, setNova] = useState('');
  const [confirma, setConfirma] = useState('');
  const [trocando, setTrocando] = useState(false);

  const fotoUrl = foto ? `/uploads/${foto}` : usuario?.foto ?? null;

  async function onArquivo(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setEnviandoFoto(true);
    try {
      const comprimido = await comprimirImagem(file);
      const fd = new FormData();
      fd.append('arquivo', comprimido);
      const resp = await api.upload<UploadFoto>('/usuarios/me/foto', fd);
      setFoto(resp.arquivo);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Falha ao enviar a foto.');
    } finally {
      setEnviandoFoto(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function salvarDados() {
    if (!nome.trim()) return toast.error('Informe o nome.');
    setSalvandoDados(true);
    try {
      await api.put('/usuarios/me', {
        nome: nome.trim(),
        email: email.trim() || null,
        foto,
      });
      await recarregar();
      toast.success('Dados atualizados.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível salvar.');
    } finally {
      setSalvandoDados(false);
    }
  }

  async function trocarSenha() {
    if (!atual || !nova) return toast.error('Preencha as senhas.');
    if (nova.length < 6) return toast.error('A nova senha precisa de ao menos 6 caracteres.');
    if (nova !== confirma) return toast.error('A confirmação não confere.');
    setTrocando(true);
    try {
      await api.post('/usuarios/me/senha', { senha_atual: atual, senha_nova: nova });
      toast.success('Senha alterada.');
      setAtual('');
      setNova('');
      setConfirma('');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível alterar a senha.');
    } finally {
      setTrocando(false);
    }
  }

  return (
    <CustomMolduraPagina
      titulo="Meu perfil"
      descricao="Seus dados de acesso e senha"
    >

      <div className="row">
        {/* Dados */}
        <div className="col-12 col-lg-8">
          <div className="card adminuiux-card mb-3 mb-lg-4">
            <div className="card-header bg-transparent">
              <h6 className="mb-0">
                <i className="bi bi-person me-2"></i>Meus dados
              </h6>
            </div>
            <div className="card-body">
              <div className="row gx-3 align-items-center mb-3">
                <div className="col-auto">
                  {fotoUrl ? (
                    <figure className="avatar avatar-80 rounded-circle coverimg mb-0">
                      <img src={fotoUrl} alt="" />
                    </figure>
                  ) : (
                    <span className="avatar avatar-80 rounded-circle bg-theme-1 bg-opacity-10 text-theme-1 d-flex align-items-center justify-content-center">
                      <i className="bi bi-person h3 mb-0"></i>
                    </span>
                  )}
                </div>
                <div className="col-auto">
                  <button
                    className="btn btn-sm btn-outline-theme"
                    onClick={() => fileRef.current?.click()}
                    disabled={enviandoFoto}
                  >
                    {enviandoFoto ? (
                      <span className="spinner-border spinner-border-sm me-1" />
                    ) : (
                      <i className="bi bi-camera me-1"></i>
                    )}
                    Trocar foto
                  </button>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    className="d-none"
                    onChange={onArquivo}
                  />
                </div>
              </div>

              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label small text-secondary">Nome</label>
                  <input
                    className="form-control"
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label small text-secondary">E-mail</label>
                  <input
                    type="email"
                    className="form-control"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label small text-secondary">Usuário</label>
                  <input className="form-control" value={usuario?.usuario ?? ''} disabled />
                </div>
                <div className="col-md-4">
                  <label className="form-label small text-secondary">Perfil</label>
                  <input
                    className="form-control"
                    value={usuario?.perfil_nome ?? usuario?.perfil ?? ''}
                    disabled
                  />
                </div>
              </div>
            </div>
            <div className="card-footer bg-transparent text-end">
              <button className="btn btn-theme" onClick={salvarDados} disabled={salvandoDados}>
                {salvandoDados && <span className="spinner-border spinner-border-sm me-1" />}
                Salvar dados
              </button>
            </div>
          </div>
        </div>

        {/* Senha */}
        <div className="col-12 col-lg-4">
          <div className="card adminuiux-card mb-3 mb-lg-4">
            <div className="card-header bg-transparent">
              <h6 className="mb-0">
                <i className="bi bi-shield-lock me-2"></i>Trocar senha
              </h6>
            </div>
            <div className="card-body">
              <div className="mb-3">
                <label className="form-label small text-secondary">Senha atual</label>
                <input
                  type="password"
                  className="form-control"
                  value={atual}
                  onChange={(e) => setAtual(e.target.value)}
                  autoComplete="current-password"
                />
              </div>
              <div className="mb-3">
                <label className="form-label small text-secondary">Nova senha</label>
                <input
                  type="password"
                  className="form-control"
                  value={nova}
                  onChange={(e) => setNova(e.target.value)}
                  autoComplete="new-password"
                />
                <div className="form-text">Mínimo de 6 caracteres.</div>
              </div>
              <div className="mb-3">
                <label className="form-label small text-secondary">Confirmar nova senha</label>
                <input
                  type="password"
                  className="form-control"
                  value={confirma}
                  onChange={(e) => setConfirma(e.target.value)}
                  autoComplete="new-password"
                />
              </div>
              <button
                className="btn btn-theme w-100"
                onClick={trocarSenha}
                disabled={trocando}
              >
                {trocando && <span className="spinner-border spinner-border-sm me-1" />}
                Alterar senha
              </button>
            </div>
          </div>
        </div>
      </div>
    </CustomMolduraPagina>
  );
}
